import React, { FC, useMemo } from 'react';
import { Box, ButtonBase, Chip, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { GridFilterModel } from '@mui/x-data-grid-pro';
import { RevisionRoles } from '../__generated__/graphql';
import { selectRevisionFilter, setFilterModel } from './filterSlice';
import { countByProgressResponsibility, ongoingFilterItem, RevisionKpi, revisionKpis } from './statistics';
import { Revision } from './types';

const kpiFilterId = (key: string) => `kpi:${key}`;
const roleFilterId = kpiFilterId('role');

const ongoingFilterId = (key: string) => `${kpiFilterId(key)}:ongoing`;

export const RevisionStatistics: FC<{ revisions: Revision[] }> = ({ revisions }) => {
  const dispatch = useDispatch();
  const { filterModel } = useSelector(selectRevisionFilter);

  const counts = useMemo(() => revisionKpis.map((kpi) => ({ kpi, count: revisions.filter(kpi.matches).length })), [revisions]);
  const roleCounts = useMemo(() => countByProgressResponsibility(revisions), [revisions]);

  const withoutItems = (model: GridFilterModel, ...ids: string[]) => model.items.filter((item) => !ids.includes(item.id as string));

  const activeRole = filterModel.items.find((item) => item.id === roleFilterId)?.value as RevisionRoles | undefined;

  const toggleKpi = (kpi: RevisionKpi) => {
    const id = kpiFilterId(kpi.key);
    const ongoingId = ongoingFilterId(kpi.key);
    const isActive = filterModel.items.some((item) => item.id === id);
    const remaining = withoutItems(filterModel, id, ongoingId);

    const items = isActive ? remaining : [...remaining, { ...kpi.filterItem, id }, { ...ongoingFilterItem, id: ongoingId }];
    dispatch(setFilterModel({ ...filterModel, items }));
  };

  const toggleRole = (role: RevisionRoles) => {
    const ongoingId = ongoingFilterId('role');
    const remaining = withoutItems(filterModel, roleFilterId, ongoingId);

    dispatch(
      setFilterModel({
        ...filterModel,
        items:
          activeRole === role
            ? remaining
            : [
                ...remaining,
                { id: roleFilterId, field: 'progressResponsibility', operator: 'includesRole', value: role },
                { ...ongoingFilterItem, id: ongoingId },
              ],
      }),
    );
  };

  const anyResponsibility = roleCounts.some((x) => x.count > 0);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: 3 }} alignItems="flex-start">
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ flexGrow: 1 }}>
          {counts.map(({ kpi, count }) => (
            <KpiCard
              key={kpi.key}
              kpi={kpi}
              count={count}
              active={filterModel.items.some((item) => item.id === kpiFilterId(kpi.key))}
              onClick={() => toggleKpi(kpi)}
            />
          ))}
        </Stack>

        <Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 0.75 }}>
            Ligger hos
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {anyResponsibility ? (
              roleCounts.map(({ role, label, count }) => (
                <Tooltip key={role} title={count === 0 ? `Ingen revisjoner ligger hos ${label}` : `${count} hos ${label}`}>
                  <Chip
                    size="small"
                    label={`${label} ${count}`}
                    variant={count === 0 ? 'outlined' : 'filled'}
                    color={activeRole === role ? 'primary' : 'default'}
                    aria-pressed={activeRole === role}
                    onClick={count === 0 ? undefined : () => toggleRole(role)}
                    sx={{ opacity: count === 0 ? 0.55 : 1 }}
                  />
                </Tooltip>
              ))
            ) : (
              <Typography variant="caption" color="textSecondary">
                Ingen av revisjonene har «Ligger hos» satt ennå.
              </Typography>
            )}
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
};

const kpiColor = (kpi: RevisionKpi, count: number) => {
  if (count === 0) return 'text.secondary';
  if (kpi.severity === 'error') return 'error.main';
  if (kpi.severity === 'warning') return 'warning.dark';
  return 'text.primary';
};

const KpiCard: FC<{ kpi: RevisionKpi; count: number; active: boolean; onClick: () => void }> = ({ kpi, count, active, onClick }) => (
  <Tooltip title={kpi.help}>
    <ButtonBase
      onClick={onClick}
      aria-pressed={active}
      sx={{
        display: 'block',
        textAlign: 'left',
        minWidth: 130,
        px: 1.5,
        py: 1,
        border: 1,
        borderRadius: 1,
        borderColor: active ? 'primary.main' : 'divider',
        backgroundColor: active ? 'action.selected' : 'transparent',
      }}>
      <Typography variant="h6" component="div" color={kpiColor(kpi, count)}>
        {count}
      </Typography>
      <Typography variant="caption" component="div" color="textSecondary">
        {kpi.label}
      </Typography>
    </ButtonBase>
  </Tooltip>
);
